import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircle, PaperPlaneRight, Calendar, X, CheckCircle } from "@phosphor-icons/react";

/**
 * TenantThreadPanel — admin/crew-facing view of the tenant portal thread
 * (Roadmap #7 Phase B) + propose visit window (Phase C).
 * Renders on JobDetail / AdminJobDetail so office/crew can respond inline.
 */
export default function TenantThreadPanel({ job, onReload, role = "admin" }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);

  const thread = job.tenant_thread || [];
  const sched = job.scheduled_window || null;
  const status = job.schedule_status || "none";

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/jobs/${job.id}/tenant-message`, { text: msg.trim() });
      setMsg("");
      onReload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSending(false); }
  };

  // Only useful when there's a tenant submitter to talk to
  if (!job.tenant_submitter_name && !job.tenant_submitter_phone) return null;

  return (
    <>
      <div className="mt-3 rugged-card p-4 bg-white border-l-4 border-cyan-500" data-testid="tenant-thread-panel">
        <div className="flex items-center gap-2 mb-2">
          <ChatCircle weight="fill" size={18} className="text-cyan-600"/>
          <h3 className="font-display font-black text-sm uppercase tracking-wider">Tenant Thread</h3>
          <span className="text-[10px] text-slate-500">
            {job.tenant_submitter_name}{job.tenant_submitter_phone ? ` · ${job.tenant_submitter_phone}` : ""}
          </span>
          {role === "admin" && (
            <button onClick={() => setProposeOpen(true)} data-testid="tenant-propose-window-btn"
              className="ml-auto inline-flex items-center gap-1 h-8 px-2 border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-800 font-black uppercase text-[10px] tracking-widest">
              <Calendar weight="fill" size={11}/> Propose Window
            </button>
          )}
        </div>

        {sched && (
          <div className={`mb-2 p-2 border-2 text-xs ${status === "confirmed" ? "border-emerald-500 bg-emerald-50" : status === "proposed" ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-slate-50"}`}
            data-testid="tenant-thread-window">
            <div className="font-black uppercase text-[9px] tracking-widest">
              Visit Window · <span className={status === "confirmed" ? "text-emerald-700" : status === "proposed" ? "text-amber-700" : "text-slate-500"}>{status}</span>
            </div>
            <div className="mt-0.5 font-black">{fmtWindow(sched)}</div>
            {job.schedule_response_note && (
              <div className="mt-1 text-[11px] text-slate-700 italic">"{job.schedule_response_note}"</div>
            )}
          </div>
        )}

        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {thread.length === 0 && (
            <div className="text-[11px] text-slate-400 italic py-2">No messages yet — tenant will see anything you send here.</div>
          )}
          {thread.map((m, i) => {
            const isCrew = m.from === "crew";
            return (
              <div key={i} data-testid={`tenant-thread-msg-${i}`}
                className={`p-2 max-w-[85%] ${isCrew ? "ml-auto bg-cyan-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                <div className="text-[9px] font-black uppercase tracking-widest opacity-70">
                  {m.author || (isCrew ? "Crew" : "Tenant")} · {m.at && new Date(m.at).toLocaleString()}
                </div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap">{m.text}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex gap-1">
          <input value={msg} onChange={e => setMsg(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Reply to tenant…" data-testid="tenant-thread-input"
            className="flex-1 h-10 px-3 border-2 border-[#0f172a] text-sm"/>
          <button onClick={send} disabled={sending || !msg.trim()}
            data-testid="tenant-thread-send"
            className="h-10 px-3 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50">
            <PaperPlaneRight weight="fill" size={12}/>
          </button>
        </div>
      </div>

      {proposeOpen && (
        <ProposeWindowModal job={job} onClose={() => setProposeOpen(false)} onDone={onReload}/>
      )}
    </>
  );
}

function ProposeWindowModal({ job, onClose, onDone }) {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const [date, setDate] = useState(today);
  const [startHr, setStartHr] = useState("10:00");
  const [endHr, setEndHr] = useState("13:00");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const start = new Date(`${date}T${startHr}:00`);
      const end = new Date(`${date}T${endHr}:00`);
      if (end <= start) { toast.error("End time must be after start"); setBusy(false); return; }
      await api.post(`/jobs/${job.id}/propose-window`, {
        start: start.toISOString(), end: end.toISOString(),
      });
      toast.success("Window proposed to tenant");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6"
        onClick={e => e.stopPropagation()} data-testid="propose-window-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar weight="fill" size={20} className="text-amber-600"/>
            <h3 className="font-display font-black text-lg uppercase">Propose Visit Window</h3>
          </div>
          <button onClick={onClose}><X weight="bold" size={20}/></button>
        </div>
        <p className="mt-2 text-xs text-slate-600">
          Tenant sees this in their portal with Accept / Reschedule / Decline buttons.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              data-testid="propose-window-date"
              className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Start</label>
              <input type="time" value={startHr} onChange={e => setStartHr(e.target.value)}
                data-testid="propose-window-start"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">End</label>
              <input type="time" value={endHr} onChange={e => setEndHr(e.target.value)}
                data-testid="propose-window-end"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1"/>
            </div>
          </div>
        </div>
        <button onClick={submit} disabled={busy}
          data-testid="propose-window-submit"
          className="mt-4 w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] inline-flex items-center justify-center gap-1 disabled:opacity-50">
          <CheckCircle weight="fill" size={14}/> {busy ? "Sending…" : "Send to Tenant"}
        </button>
      </div>
    </div>
  );
}

function fmtWindow(w) {
  if (!w) return "";
  try {
    const s = new Date(w.start), e = new Date(w.end);
    const day = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const st = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const et = e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${day} · ${st} – ${et}`;
  } catch { return w.start; }
}
